import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, AtSign, Mail, Lock, Building2, User, Briefcase } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import logo from '@/assets/logo.png';

interface ModernAuthLayoutProps {
  // Auth state
  isLoading: boolean;
  error: string | null;
  userEmail: string;
  hasValidInvitation: boolean | null;
  checkingInvitation: boolean;
  invitationEmail: string | null;
  invitationToken: string | null;
  defaultTab: string;
  defaultSignupTab: string;

  // Handlers
  handleSignIn: (e: React.FormEvent<HTMLFormElement>) => void;
  handleSignUp: () => void;
  handleUserSignUp: () => void;
  handleEmailChange: (email: string) => void;
}

export function ModernAuthLayout({
  isLoading,
  error,
  userEmail,
  hasValidInvitation,
  checkingInvitation,
  invitationEmail,
  invitationToken,
  defaultTab,
  defaultSignupTab,
  handleSignIn,
  handleSignUp,
  handleUserSignUp,
  handleEmailChange
}: ModernAuthLayoutProps) {
  return (
    <main className="relative md:h-screen md:overflow-hidden lg:grid lg:grid-cols-2">
      {/* Left Panel - Branding & Animation */}
      <div className="bg-muted/60 relative hidden h-full flex-col border-r p-10 lg:flex">
        <div className="from-background absolute inset-0 z-10 bg-gradient-to-t to-transparent" />
        
        {/* Logo */}
        <motion.div
          className="z-10 flex items-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Link to="/welcome" className="flex items-center gap-3">
            <img src={logo} alt="Manageaze Logo" className="size-8" />
            <p className="text-xl font-semibold text-foreground">Manageaze</p>
          </Link>
        </motion.div>
        
        {/* Quote */}
        <motion.div 
          className="z-10 mt-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <blockquote className="space-y-2">
            <p className="text-xl text-foreground">
              &ldquo;This platform has revolutionized our policy management, 
              making compliance effortless and collaboration seamless.&rdquo;
            </p>
            <footer className="font-mono text-sm font-semibold text-muted-foreground">
              ~ Sortitt
            </footer>
          </blockquote>
        </motion.div>
        
        {/* Animated Background */}
        <div className="absolute inset-0">
          <FloatingPaths position={1} />
          <FloatingPaths position={-1} />
        </div>
      </div>

      {/* Right Panel - Auth Forms */}
      <div className="relative flex min-h-screen flex-col justify-center p-4">
        {/* Background Decorations */}
        <div
          aria-hidden
          className="absolute inset-0 isolate contain-strict -z-10 opacity-60"
        >
          <div className="bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,--theme(--color-foreground/.06)_0,hsla(0,0%,55%,.02)_50%,--theme(--color-foreground/.01)_80%)] absolute top-0 right-0 h-320 w-140 -translate-y-87.5 rounded-full" />
          <div className="bg-[radial-gradient(50%_50%_at_50%_50%,--theme(--color-foreground/.04)_0,--theme(--color-foreground/.01)_80%,transparent_100%)] absolute top-0 right-0 h-320 w-60 [translate:5%_-50%] rounded-full" />
          <div className="bg-[radial-gradient(50%_50%_at_50%_50%,--theme(--color-foreground/.04)_0,--theme(--color-foreground/.01)_80%,transparent_100%)] absolute top-0 right-0 h-320 w-60 -translate-y-87.5 rounded-full" />
        </div>

        {/* Mobile Logo */}
        <motion.div
          className="flex items-center lg:hidden mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Link to="/welcome" className="flex items-center gap-3">
            <img src={logo} alt="Manageaze Logo" className="size-6" />
            <p className="text-xl font-semibold text-foreground">Manageaze</p>
          </Link>
        </motion.div>

        {/* Auth Content */}
        <div className="mx-auto space-y-6 sm:w-sm">
          <motion.div 
            className="flex flex-col space-y-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h1 className="font-heading text-3xl font-bold tracking-wide text-foreground">
              Welcome to Manageaze
            </h1>
            <p className="text-muted-foreground text-base">
              Sign in to your account or create a new one to get started.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Tabs defaultValue={defaultTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>

              {/* Sign In Tab */}
              <TabsContent value="signin" className="space-y-4">
                <motion.form 
                  onSubmit={handleSignIn} 
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                >
                  <motion.div 
                    className="relative h-max"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 }}
                  >
                    <Input
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      className="peer ps-9"
                      required
                    />
                    <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                      <Mail className="size-4" aria-hidden="true" />
                    </div>
                  </motion.div>

                  <motion.div 
                    className="relative h-max"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.7 }}
                  >
                    <Input
                      name="password"
                      type="password"
                      placeholder="Enter your password"
                      className="peer ps-9"
                      required
                    />
                    <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                      <Lock className="size-4" aria-hidden="true" />
                    </div>
                  </motion.div>

                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Alert variant="destructive">
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.8 }}
                  >
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </motion.div>
                </motion.form>
              </TabsContent>

            {/* Sign Up Tab */}
            <TabsContent value="signup" className="space-y-4">
              <Tabs defaultValue={defaultSignupTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="admin">Admin</TabsTrigger>
                  <TabsTrigger value="user">User</TabsTrigger>
                </TabsList>

                {/* Admin Signup */}
                <TabsContent value="admin" className="space-y-4 mt-4">
                  <motion.div 
                    className="text-sm text-muted-foreground bg-muted p-3 rounded-md"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                  >
                    Create a new company and become its admin. Company name must be unique.
                  </motion.div>

                  <div className="space-y-4">
                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.6 }}
                    >
                      <Input
                        id="admin-signup-name"
                        name="name"
                        type="text"
                        placeholder="Enter your full name"
                        className="peer ps-9"
                        required
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <User className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.7 }}
                    >
                      <Input
                        id="admin-signup-company"
                        name="company"
                        type="text"
                        placeholder="Enter your company name"
                        className="peer ps-9"
                        required
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <Building2 className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.8 }}
                    >
                      <Input
                        id="admin-signup-email"
                        name="email"
                        type="email"
                        placeholder="your.email@example.com"
                        className="peer ps-9"
                        required
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <Mail className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.9 }}
                    >
                      <Input
                        id="admin-signup-designation"
                        name="designation"
                        type="text"
                        placeholder="Your job title (optional)"
                        className="peer ps-9"
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <Briefcase className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 1.0 }}
                    >
                      <Input
                        id="admin-signup-password"
                        name="password"
                        type="password"
                        placeholder="Create a password"
                        className="peer ps-9"
                        required
                        minLength={6}
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <Lock className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <AnimatePresence>
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                          </Alert>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 1.1 }}
                    >
                      <Button onClick={handleSignUp} className="w-full" disabled={isLoading}>
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creating admin account...
                          </>
                        ) : (
                          'Create Admin Account'
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </TabsContent>

                {/* User Signup */}
                <TabsContent value="user" className="space-y-4 mt-4">
                  <motion.div 
                    className="text-sm text-muted-foreground bg-muted p-3 rounded-md"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.5 }}
                  >
                    Join an existing company using an invitation from your admin.
                  </motion.div>

                  <div className="space-y-4">
                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.6 }}
                    >
                      <Input
                        id="user-signup-name"
                        name="name"
                        type="text"
                        placeholder="Enter your full name"
                        className="peer ps-9"
                        required
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <User className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.7 }}
                    >
                      <Input
                        id="user-signup-email"
                        name="email"
                        type="email"
                        placeholder="your.email@example.com"
                        className="peer ps-9"
                        value={invitationEmail || userEmail}
                        onChange={e => handleEmailChange(e.target.value)}
                        readOnly={!!invitationEmail}
                        required
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <Mail className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <AnimatePresence>
                      {checkingInvitation && (
                        <motion.div 
                          className="flex items-center gap-2 text-sm text-muted-foreground"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Checking invitation...
                        </motion.div>
                      )}

                      {hasValidInvitation === false && userEmail && (
                        <motion.div 
                          className="text-sm text-destructive"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          No valid invitation found for this email. Please ask your admin to invite you.
                        </motion.div>
                      )}

                      {hasValidInvitation === true && (
                        <motion.div 
                          className="text-sm text-green-600"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          ✓ Valid invitation found! You can proceed with signup.
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.8 }}
                    >
                      <Input
                        id="user-signup-password"
                        name="password"
                        type="password"
                        placeholder="Create a password"
                        className="peer ps-9"
                        required
                        minLength={6}
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <Lock className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <motion.div 
                      className="relative h-max"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.9 }}
                    >
                      <Input
                        id="user-signup-token"
                        name="invitationToken"
                        type="text"
                        placeholder="Enter your invitation token"
                        value={invitationToken || ''}
                        readOnly={!!invitationToken}
                        className="peer ps-9"
                        required
                      />
                      <div className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                        <AtSign className="size-4" aria-hidden="true" />
                      </div>
                    </motion.div>

                    <motion.p 
                      className="text-xs text-muted-foreground"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 1.0 }}
                    >
                      This token was sent to you via email invitation
                    </motion.p>

                    <AnimatePresence>
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                          </Alert>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 1.1 }}
                    >
                      <Button 
                        onClick={handleUserSignUp} 
                        className="w-full" 
                        disabled={isLoading || hasValidInvitation === false || checkingInvitation}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Joining company...
                          </>
                        ) : hasValidInvitation === false ? (
                          'No Valid Invitation'
                        ) : (
                          'Join Company'
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </TabsContent>
              </Tabs>
            </TabsContent>
            </Tabs>
          </motion.div>

          <motion.p 
            className="text-muted-foreground text-sm text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            By continuing, you agree to our{' '}
            <a href="https://www.manageaze.com/terms" className="hover:text-primary underline underline-offset-4">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://www.manageaze.com/privacy" className="hover:text-primary underline underline-offset-4">
              Privacy Policy
            </a>
            .
          </motion.p>
        </div>
      </div>
    </main>
  );
}

function FloatingPaths({ position }: { position: number }) {
  const paths = Array.from({ length: 36 }, (_, i) => ({
    id: i,
    d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${
      380 - i * 5 * position
    } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${
      152 - i * 5 * position
    } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${
      684 - i * 5 * position
    } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
    color: `rgba(15,23,42,${0.1 + i * 0.03})`,
    width: 0.5 + i * 0.03,
  }));

  return (
    <div className="pointer-events-none absolute inset-0">
      <svg
        className="h-full w-full text-slate-950 dark:text-white"
        viewBox="0 0 696 316"
        fill="none"
      >
        <title>Background Paths</title>
        {paths.map((path) => (
          <motion.path
            key={path.id}
            d={path.d}
            stroke="currentColor"
            strokeWidth={path.width}
            strokeOpacity={0.1 + path.id * 0.03}
            initial={{ pathLength: 0.3, opacity: 0.6 }}
            animate={{
              pathLength: 1,
              opacity: [0.3, 0.6, 0.3],
              pathOffset: [0, 1, 0],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'linear',
            }}
          />
        ))}
      </svg>
    </div>
  );
}
